import { useMemo, useState, useCallback } from 'react'
import {
  useRouteLoaderData,
  useSearchParams,
  useLoaderData,
  useParams,
  useNavigate,
  Outlet,
} from 'react-router-dom'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from 'react-i18next'
import {
  GridColDef,
  GridInitialState,
  GridRowModesModel,
  GridRowModes,
} from '@mui/x-data-grid-premium'
import { toast } from 'react-toastify'
import upperFirst from 'lodash/upperFirst'

import { LoaderData, Sponsorship } from '../../../types'
import {
  useActivityOptions,
  useSponsorshipStatusOptions,
  useSponsorshipTypeOptions,
  useLaboratoriesOptions,
  useMembershipLevelOptions,
  useJobTitlesDataOptions,
  formatDateTime,
} from '../../../utils'
import { adminEventDetailsLoader, adminSponsorshipLoader } from '../Admin.loader'
import SponsorshipStatusTag from '../../abstracts/components/SponsorshipStatusTag'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import { useContextShowStatistics } from '../Admin.Context'
import AdminSponsorshipDetails from './components/AdminSponsorshipDetails'
import ViewButtonGroup from './components/ViewButtonGroup'
import BooleanRender from './components/BooleanRender'

type Row = Sponsorship<true>

const initialState: GridInitialState = {
  sorting: {
    sortModel: [
      {
        field: 'sub_event_type',
        sort: 'desc',
      },
    ],
  },
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
}

export default function AdminSponsorship() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { eventId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const { event } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const { sponsorship, sponsorshipStats } = useLoaderData() as LoaderData<
    ReturnType<typeof adminSponsorshipLoader>
  >
  const data = sponsorship?.results satisfies Row[] | undefined

  const membershipLevelOptions = useMembershipLevelOptions(t)
  const jobTitlesDataOptions = useJobTitlesDataOptions()
  const laboratoriesOptions = useLaboratoriesOptions()
  const statusOptions = useSponsorshipStatusOptions(t)
  const sponsorshipTypesOptions = useSponsorshipTypeOptions()
  const activityOptions = useActivityOptions(t)

  const onEdit = useCallback(
    (id: number) => {
      navigate(`/admin/details/${eventId}/sponsorship/${id}?${searchParams.toString()}`)
    },
    [navigate, eventId, searchParams]
  )

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          return [
            <Tooltip title={t('Modifier prise en charge')}>
              <IconButton
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onEdit(params.row.id)
                }}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>,
          ]
        },
      },
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'created',
        headerName: t('Démandé le'),
        width: 150,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: value => (value ? formatDateTime(value) : 'NC'),
      },
      {
        field: 'laboratory',
        headerName: t('Laboratoire'),
        width: 200,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: laboratoriesOptions,
      },
      {
        field: 'activity',
        headerName: t('Activité'),
        width: 150,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: activityOptions,
      },
      {
        field: 'status',
        headerName: t('Statut prise en charge'),
        width: 200,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: statusOptions,
        renderCell: params => (
          <SponsorshipStatusTag size="small" status={params.value as Row['status']} />
        ),
      },
      {
        field: 'type',
        headerName: t('Type'),
        width: 125,
        type: 'singleSelect',
        valueOptions: sponsorshipTypesOptions,
      },
      {
        field: 'last_name',
        headerName: t('Nom'),
        width: 150,
        valueGetter: (_value, params) => params.user?.last_name,
      },
      {
        field: 'first_name',
        headerName: t('Prénom'),
        width: 150,
        valueGetter: (_value, params) => params.user?.first_name,
      },
      {
        field: 'terms_accepted',
        headerName: t('Conditions acceptées'),
        width: 150,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'request_registration_costs',
        headerName: t('Frais d’inscription'),
        width: 150,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'request_accommodation_costs',
        headerName: t('Frais d’hébergement'),
        width: 150,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'request_travel_costs',
        headerName: t('Frais de déplacement'),
        width: 150,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'age',
        headerName: t('Âge'),
        width: 100,
        valueFormatter: (value: number | null) => (value ? `${value} ans` : 'NC'),
      },
      {
        field: 'membership_level',
        headerName: t('Statut du membre'),
        type: 'singleSelect',
        valueOptions: membershipLevelOptions,
        width: 150,
        valueFormatter: value =>
          membershipLevelOptions.find(option => option.value === value)?.label ?? 'NC',
      },
      {
        field: 'home_region',
        headerName: t('Région'),
        width: 100,
        valueFormatter: value => value ?? 'NC',
      },
      {
        field: 'service_name',
        headerName: t('Service'),
        width: 300,
      },
      {
        field: 'job_title',
        headerName: t('Type de poste'),
        type: 'singleSelect',
        valueOptions: jobTitlesDataOptions,
        width: 250,
        valueFormatter: value =>
          upperFirst(jobTitlesDataOptions.find(option => option.value === value)?.label ?? 'NC'),
      },
    ],
    [
      t,
      laboratoriesOptions,
      activityOptions,
      statusOptions,
      sponsorshipTypesOptions,
      membershipLevelOptions,
      jobTitlesDataOptions,
      onEdit,
    ]
  )

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails événement')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminSponsorshipDetails statistics={sponsorshipStats} event={event} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <h3>{t('Liste des prises en charge')} :</h3>
          </Stack>

          <ViewButtonGroup />
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            editMode="row"
            rowModesModel={rowModesModel}
            rows={data}
            columns={columns}
            initialState={initialState}
            count={sponsorship?.count}
            paginationMode="server"
            // editOptions={{
            //   onClick: selectedRows => {
            //     const newRowModelProps: GridRowModesModel = [...selectedRows].reduce(
            //       (acc, [rowId]) => {
            //         acc[rowId] = { mode: GridRowModes.Edit }
            //         return acc
            //       },
            //       {} as GridRowModesModel
            //     )
            //     setRowModesModel({
            //       ...rowModesModel,
            //       ...newRowModelProps,
            //     })
            //   },
            // }}
          />
        </Stack>
      </Stack>

      <Outlet />
    </Stack>
  )
}
